// The Capability map's GROUPED PANE (Capability Map v2, the "Signal" re-layout): the pane header (title,
// honest note, the By-permission | By-area toggle) and one card per group — the group head (bucket dot,
// label, count, plain-English note) and one expandable row per capability. A row shows the registry's own
// operational summary (G-169); expanding it restates the recorded flags as meta chips (who runs it, the
// effect, how it is reached, the owning area, the registry id) — nothing an entry does not carry renders
// (P-SELF, G-182). Long groups fold behind an honest "Show the other N". Tokens only (G-181).

import type { CapabilityEntryView } from '../operate-projections.ts';
import {
  BUCKET_META,
  areaLabelOf,
  bucketOf,
  kindLabelOf,
  type CapabilityBucket,
} from './capability-buckets.ts';

export const GROUP_LIMIT = 10;

/** One rendered group: its identity, tone bucket (null = neutral area group), note, and items. */
export interface CapabilityGroupView {
  readonly key: string;
  readonly label: string;
  readonly bucket: CapabilityBucket | null;
  readonly note: string | null;
  readonly items: readonly CapabilityEntryView[];
}

/** The expanded row's meta chips — each one a restatement of a field the entry carries. */
function RowMeta({ cap }: { readonly cap: CapabilityEntryView }): React.JSX.Element {
  const pairs: readonly (readonly [string, string])[] = [
    ['Who runs it', BUCKET_META[bucketOf(cap)].who],
    ['Effect', cap.effect === 'write' ? 'Writes / changes data' : 'Reads data only'],
    ['Reached as', kindLabelOf(cap.kind)],
    ['Lives in', areaLabelOf(cap.owner)],
    ['Registry id', cap.id],
  ];
  return (
    <div className="cm-meta">
      {pairs.map(([key, value]) => (
        <span key={key} className="cm-meta-chip">
          <span className="k">{key}</span>
          <span className="v">{value}</span>
        </span>
      ))}
    </div>
  );
}

/** One expandable capability row. */
function CapabilityRow({
  cap,
  showAreaChip,
  open,
  onToggle,
}: {
  readonly cap: CapabilityEntryView;
  readonly showAreaChip: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  return (
    <div className="cm-rowwrap" data-capability={cap.id} data-bucket={bucketOf(cap)}>
      <button type="button" className="cm-row" aria-expanded={open} onClick={onToggle}>
        <span className="dot" aria-hidden="true" />
        <span className="cm-row-main">
          <span className="sum">{cap.summary}</span>
        </span>
        {showAreaChip ? <span className="cm-row-area">{areaLabelOf(cap.owner)}</span> : null}
        <span className={open ? 'cm-chev open' : 'cm-chev'} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="cm-detail">
          <RowMeta cap={cap} />
        </div>
      ) : null}
    </div>
  );
}

/** One group card: the toned head + its rows, folded behind an honest show-more past the limit. */
function GroupCard({
  group,
  showAreaChip,
  openId,
  onToggleRow,
  expanded,
  onMore,
}: {
  readonly group: CapabilityGroupView;
  readonly showAreaChip: boolean;
  readonly openId: string | null;
  readonly onToggleRow: (id: string) => void;
  readonly expanded: boolean;
  readonly onMore: () => void;
}): React.JSX.Element {
  const foldable = group.items.length > GROUP_LIMIT + 2;
  const shown = !foldable || expanded ? group.items : group.items.slice(0, GROUP_LIMIT);
  const hidden = group.items.length - shown.length;
  return (
    <div className="cm-group" data-group={group.key}>
      <div className="cm-group-head" data-bucket={group.bucket ?? 'none'}>
        <span className="dot" aria-hidden="true" />
        <span className="cm-group-id">
          <span className="cm-group-line">
            <span className="lb">{group.label}</span>
            <span className="cnt">
              {group.items.length} {group.items.length === 1 ? 'capability' : 'capabilities'}
            </span>
          </span>
          {group.note !== null ? <span className="cm-group-note">{group.note}</span> : null}
        </span>
      </div>
      {shown.map((cap) => (
        <CapabilityRow
          key={cap.id}
          cap={cap}
          showAreaChip={showAreaChip}
          open={openId === cap.id}
          onToggle={() => onToggleRow(cap.id)}
        />
      ))}
      {foldable ? (
        <button type="button" className="cm-more" onClick={onMore}>
          {hidden > 0 ? `Show the other ${hidden}` : 'Show fewer'}
        </button>
      ) : null}
    </div>
  );
}

/** The pane header: the title + honest note and the group-mode toggle. */
function PaneHeader({
  title,
  note,
  mode,
  onMode,
}: {
  readonly title: string;
  readonly note: string;
  readonly mode: 'permission' | 'area';
  readonly onMode: (mode: 'permission' | 'area') => void;
}): React.JSX.Element {
  return (
    <div className="cm-pane-head">
      <h2 className="cm-pane-title">{title}</h2>
      <span className="cm-pane-note">{note}</span>
      <span className="spacer" aria-hidden="true" />
      <div role="group" aria-label="Group by" className="rt-seg">
        <button
          type="button"
          className="sg"
          aria-pressed={mode === 'permission'}
          onClick={() => onMode('permission')}
        >
          By permission
        </button>
        <button
          type="button"
          className="sg"
          aria-pressed={mode === 'area'}
          onClick={() => onMode('area')}
        >
          By area
        </button>
      </div>
    </div>
  );
}

/** The honest empty state — what is absent and, when a filter caused it, the way back. */
function EmptyCard({
  title,
  note,
  onClearFilter,
}: {
  readonly title: string;
  readonly note: string;
  readonly onClearFilter: (() => void) | null;
}): React.JSX.Element {
  return (
    <div className="cm-empty">
      <span className="cm-empty-title">{title}</span>
      <span className="cm-empty-note">{note}</span>
      {onClearFilter !== null ? (
        <button type="button" className="btn ghost sm" onClick={onClearFilter}>
          Clear the filter
        </button>
      ) : null}
    </div>
  );
}

/** The grouped capability pane, or the honest empty state when nothing matches the filters. */
export function CapabilityGroups({
  title,
  note,
  mode,
  onMode,
  groups,
  openId,
  onToggleRow,
  more,
  onMore,
  emptyTitle,
  emptyNote,
  onClearFilter,
}: {
  readonly title: string;
  readonly note: string;
  readonly mode: 'permission' | 'area';
  readonly onMode: (mode: 'permission' | 'area') => void;
  readonly groups: readonly CapabilityGroupView[];
  readonly openId: string | null;
  readonly onToggleRow: (id: string) => void;
  readonly more: Readonly<Record<string, boolean>>;
  readonly onMore: (key: string) => void;
  readonly emptyTitle: string;
  readonly emptyNote: string;
  readonly onClearFilter: (() => void) | null;
}): React.JSX.Element {
  return (
    <section aria-label="Capability map" className="cm-pane">
      <PaneHeader title={title} note={note} mode={mode} onMode={onMode} />
      {groups.map((group) => (
        <GroupCard
          key={group.key}
          group={group}
          showAreaChip={mode === 'permission'}
          openId={openId}
          onToggleRow={onToggleRow}
          expanded={more[group.key] === true}
          onMore={() => onMore(group.key)}
        />
      ))}
      {groups.length === 0 ? (
        <EmptyCard title={emptyTitle} note={emptyNote} onClearFilter={onClearFilter} />
      ) : null}
      <ProvenanceFoot />
    </section>
  );
}

/** The provenance line — the map's honest generation claim (P-MAPPED, G-148). */
function ProvenanceFoot(): React.JSX.Element {
  return (
    <p className="cm-foot">
      Every capability on this page is generated from Ritona&rsquo;s own source code, so the list
      itself cannot fall out of date. Each summary is the registry&rsquo;s own operational wording —
      nothing here is hand-kept.
    </p>
  );
}
